import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CSVImportSection = ({ onFileUpload, importHistory, onDownloadErrorReport }) => {
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [mappingConfig, setMappingConfig] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fieldMappingOptions = [
    { value: 'employee_id', label: 'Employee ID' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'email', label: 'Email Address' },
    { value: 'department', label: 'Department' },
    { value: 'job_title', label: 'Job Title' },
    { value: 'salary', label: 'Base Salary' },
    { value: 'bonus', label: 'Bonus Amount' },
    { value: 'hire_date', label: 'Hire Date' },
    { value: 'gender', label: 'Gender' },
    { value: 'pay_band', label: 'Pay Band' },
    { value: 'skip', label: 'Skip Field' }
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    const files = e?.dataTransfer?.files;
    if (files && files?.[0]) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleFileSelection = (file) => {
    if (file?.type === 'text/csv' || file?.name?.endsWith('.csv')) {
      setSelectedFile(file);
      // Simulate CSV parsing to show column headers
      const mockColumns = ['employee_id', 'name', 'department', 'salary', 'gender'];
      const initialMapping = {};
      mockColumns?.forEach((col, index) => {
        initialMapping[col] = fieldMappingOptions?.find(opt => 
          opt?.label?.toLowerCase()?.includes(col?.toLowerCase())
        )?.value || 'skip';
      });
      setMappingConfig(initialMapping);
    } else {
      alert('Please select a CSV file');
    }
  };

  const handleFileInputChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleMappingChange = (column, value) => {
    setMappingConfig(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    await onFileUpload(selectedFile, mappingConfig);
    setIsUploading(false);
    setSelectedFile(null);
    setMappingConfig({});
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return 'CheckCircle';
      case 'error': return 'XCircle';
      case 'processing': return 'Clock';
      default: return 'FileText';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-success';
      case 'error': return 'text-destructive';
      case 'processing': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('dataIntegration.csv.sectionTitle')}</h3>
        
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive 
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">
            Drop your CSV file here
          </h4>
          <p className="text-muted-foreground mb-4">
            or click to browse and select a file
          </p>
          <Button
            variant="outline"
            iconName="FolderOpen"
            iconPosition="left"
            onClick={() => fileInputRef?.current?.click()}
          >
            Browse Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>

        {selectedFile && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
              <Icon name="FileText" size={20} className="text-muted-foreground" />
              <div className="flex-1">
                <p className="font-medium text-foreground">{selectedFile?.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(selectedFile?.size / 1024)?.toFixed(1)} KB
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={() => {
                  setSelectedFile(null);
                  setMappingConfig({});
                }}
              />
            </div>

            {/* Field Mapping */}
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">{t('dataIntegration.csv.mapCols')}</h4>
              {Object.keys(mappingConfig)?.map((column) => (
                <div key={column} className="flex items-center space-x-4">
                  <div className="w-32">
                    <p className="text-sm font-medium text-foreground">{column}</p>
                  </div>
                  <div className="flex-1">
                    <Select
                      options={fieldMappingOptions}
                      value={mappingConfig?.[column]}
                      onChange={(value) => handleMappingChange(column, value)}
                      placeholder={t('dataIntegration.csv.selectField')}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-3 pt-4">
              <Button
                variant="default"
                loading={isUploading}
                iconName="Upload"
                iconPosition="left"
                onClick={handleUpload}
              >
                Import Data
              </Button>
              <Button
                variant="outline"
                iconName="Eye"
                iconPosition="left"
              >
                Preview Data
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Import History */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('dataIntegration.csv.importHistory')}</h3>
        
        <div className="space-y-3">
          {importHistory?.map((import_) => (
            <div key={import_?.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon 
                  name={getStatusIcon(import_?.status)} 
                  size={20} 
                  className={getStatusColor(import_?.status)} 
                />
                <div>
                  <p className="font-medium text-foreground">{import_?.filename}</p>
                  <p className="text-sm text-muted-foreground">
                    {import_?.timestamp} • {import_?.recordsProcessed} records
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {import_?.status === 'success' && (
                  <span className="text-sm text-success font-medium">
                    {import_?.recordsImported} imported
                  </span>
                )}
                {import_?.status === 'error' && (
                  <span className="text-sm text-destructive font-medium">
                    {import_?.errorCount} errors
                  </span>
                )}
                {import_?.errorReport && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={() => onDownloadErrorReport(import_?.id)}
                  >
                    Error Report
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CSVImportSection;